import type { ReactNode } from 'react';
import { Tag, Typography } from 'antd';

const { Title, Paragraph } = Typography;

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  /** Teaching copy — say what this surface is FOR, not just that it is empty (spec §4.2). */
  description: ReactNode;
  /** e.g. "PS-3" — sets expectation for when this ships. */
  wave?: string;
  /** What the surface will do, as a short teach-list. */
  bullets?: string[];
  action?: ReactNode;
  /** Rendered inside a table body rather than on a bare page: same copy, tighter frame. */
  compact?: boolean;
}

/**
 * The empty-state-that-teaches. Never a blank panel: an empty surface explains its purpose and,
 * for not-yet-built areas, which Product Surface wave delivers it.
 */
export function EmptyState({
  icon,
  title,
  description,
  wave,
  bullets,
  action,
  compact,
}: EmptyStateProps) {
  return (
    <div
      role="region"
      aria-label={title}
      style={{
        maxWidth: 560,
        margin: compact ? '20px auto' : '10vh auto',
        textAlign: 'center',
        padding: '0 24px',
      }}
    >
      {icon && (
        <div
          style={{ fontSize: compact ? 26 : 40, color: 'var(--ol-text-tertiary)', marginBottom: compact ? 8 : 12 }}
          aria-hidden
        >
          {icon}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        {wave && <Tag color="processing">Coming in {wave}</Tag>}
      </div>
      <Paragraph type="secondary" style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.6 }}>
        {description}
      </Paragraph>
      {bullets && bullets.length > 0 && (
        <ul
          style={{
            textAlign: 'left',
            display: 'inline-block',
            margin: '8px auto 0',
            color: 'var(--ol-text-secondary)',
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {action && <div style={{ marginTop: 20 }}>{action}</div>}
    </div>
  );
}
